import { ClassType, Cover } from 'types/cover';
import { PaymentMethodOptions } from 'types/payments';
import {
  RiskClassCode,
  RiskDetailBPKEmployeeDishonestyTaxAuditTransit,
  RiskDetailBPKFireBusinessInterruption,
  RiskDetailBPKGeneralPropertyMachineryBreakdown,
  RiskDetailBPKLiability,
  RiskDetailBPKTheftMoneyGlass,
} from 'types/risk';

export interface PolicyRequest {
  policy: Policy;
}

export interface PolicyResponse {
  messageMetadata: MessageMetadata;
  policy: Policy;
}

export interface MessageMetadata {
  messageHeader: {
    status: string;
  };
  transactionInformation?: {
    identifier?: string;
  };
}

export interface Policy {
  policyHeader: PolicyHeader;
  customerParty: CustomerParty;
  risks: Risk[];
  questions: Question[];
  policyPremium?: PolicyPremium;
  payment?: Payment;
}

/* Start PolicyHeader */
export interface PolicyHeader {
  policyTransactionInformation: PolicyTransactionInformation;
  issueDocument?: boolean;
}

export interface PolicyTransactionInformation {
  inceptionDate: string;
  effectiveDate?: string;
  expiryDate?: string;
  product?: string;
  policyNumber?: string;
  policyStatus?: string;
  policyMessageStatus?: string;
  extendedStatus?: {
    status: string;
    message: string;
  }[];
}

/* End PolicyHeader */

/* Start CustomerParty */
export interface CustomerParty {
  firstName?: string;
  lastName?: string;
  accessToken?: string;
  taxIndicator?: string;
  taxPercent?: number;
  orgName?: string;
  orgABN?: string;
  businessName?: string;
  tradingName?: string;
  businessEntity?: string;
  emailNotification?: string;
  contact?: Contact;
  location?: CustomerPartyLocation;
  /** Partner client member ship no. I.e. costco membership number for costco */
  clientIdMemberNo?: string;
  intermediaryCode?: string;
}

export interface Contact {
  primaryEmailAddress?: string;
  mobilePhoneNumber?: string;
  workPhoneNumber?: string;
  homePhoneNumber?: string;
}

export interface CustomerPartyLocation {
  moniker?: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  city: string;
  stateCode: string;
  postalCode: string;
}

/* End CustomerParty */

/* Start Risk */
export interface Risk<DETAIL = unknown> {
  partnerId: string;
  riskAttachmentDate?: string;
  riskClassCode: RiskClassCode;
  riskEffectiveDate: string;
  terminationDate?: string;
  riskMode: string;
  riskNumber: number;
  riskReferenceId?: string;
  clauses?: Clause[];
  location: RiskLocation;
  premium?: RiskPremium;
  interestedParties?: InterestedParty[];
  riskDetail: DETAIL;
}

export type PortableItemsRisk = Risk<RiskDetailBPKGeneralPropertyMachineryBreakdown>;

export type RiskClassCodeToRiskMap = {
  [RiskClassCode.Liability]: Risk<RiskDetailBPKLiability>;
  [RiskClassCode.PortableItems]: Risk<RiskDetailBPKGeneralPropertyMachineryBreakdown>;
  [RiskClassCode.BuildingAndContents]: Risk<RiskDetailBPKFireBusinessInterruption>;
  [RiskClassCode.TheftMoneyGlass]: Risk<RiskDetailBPKTheftMoneyGlass>;
  [RiskClassCode.TaxAudit]: Risk<RiskDetailBPKEmployeeDishonestyTaxAuditTransit>;
};

export interface Clause {
  clauseWording: ClauseWording[];
}

export interface ClauseWording {
  effectiveDate?: string;
  riskClass?: string;
  premiumClass: ClassType;
  clauseCode?: string;
  clauseDescription?: string;
  clauseWordingLines?: {
    wording?: string;
  }[];
  clauseWordingVariables?: ClauseWordingVariable[];
}

export interface ClauseWordingVariable {
  title: string;
  type: string;
  length: number;
  indent: number;
  data: string;
}

export interface RiskLocation {
  partnerId?: string;
  /** @clarify This should be optional according to the RAML */
  situationNumber: string;
  situationLocated?: string;
  situationTurnover?: number;
  primaryLocation?: boolean;
  addressLine1: string;
  city: string;
  stateCode: string;
  postalCode: string;
}

export interface RiskPremium {
  brokerFee?: number;
  brokerFeeGST?: number;
  premiumClass: PremiumClass[];
}

export interface PremiumClass {
  FAPPremium: Premium;
  totalPostedToDatePremium: Premium;
  transactionPremium: Premium;
  classType: ClassType;
}

export interface Premium {
  premiumClass?: string;
  premium: number;
  GST: number;
  FSL: number;
  brokerFee?: number;
  brokerFeeGST?: number;
  duty: number;
  totalPremium: number;
  agentComm?: number;
  agentCommRate?: number;
  agentCommGST?: number;
  adjustmentIndicator?: boolean;
  adjustmentAmount?: number;
  adjustmentPercentage?: number;
}

/* End Risk */

/* Start Question */
export interface Question {
  questionId: string;
  answer: {
    text: string;
    value?: string;
  };
}

/* End Question */

/* Start PolicyPremium */
export interface PolicyPremium {
  stampDutyExemptIndicator?: string;
  FAPPremium?: Premium;
  totalPostedToDatePremium?: Premium;
  transactionPremium?: Premium;
}

/* End PolicyPremium */

/* Start Payment */
export interface Payment {
  paymentMethod?: PaymentMethodOptions;
  paymentPeriod?: string;
  deductionDate?: string;
  accountName?: string;
  accountNumber?: string;
  bsb?: string;
  creditCardNumber?: string;
  tokenizedCreditCardNumber?: string;
  creditCardType?: string;
  creditCardExpiry?: string;
  directDebitAuthorisation?: string;
  totalPremiumPaid?: number;
  instalments?: Instalment[];
  transactionId?: string;
  reasonCode?: string;
  totalAmount?: number;
  cardType?: string;
  status?: 'AUTHORIZED' | 'CAPTURED';
  transientToken?: string;
}

export interface Instalment {
  instalmentNumber: number;
  instalmentDate: string;
  instalmentStatus: string;
  instalmentStatusDesc: string;
  instalmentPremium: number;
  instalmentStampDuty: number;
  instalmentLevies: number;
  instalmentGST: number;
  instalmentCommission: number;
  instalmentCommissionGST: number;
  instalmentFee: number;
  instalmentFeeGST: number;
}

/* End Payment */

/** @todo move this into the right section when we get the MS api */

export interface InterestedPartyUI {
  id: string;
  cover: Cover;
  name: string;
}

export interface InterestedParty {
  name: string;
}

export enum PolicyBuilderMode {
  Premiums,
  Quote,
  Bind,
}
